import type { ReactElement } from "react";

import { Skeleton } from "../ui/skeleton";
import { PluginRow } from "./PluginRow";
import { PLUGIN_ROW_CLASS, PLUGIN_ROW_CONTROL_CLASS } from "./pluginRowChrome";
import {
  type ForgePlugin,
  type ForgePluginId,
  type PluginConnectionAttempt,
} from "./pluginsPage.logic";
import type { PluginConnectionAttempts } from "./pluginConnections";

const EMPTY_PLUGIN_CONNECTION_ATTEMPTS: PluginConnectionAttempts = {};

export interface PluginConnectionListProps {
  readonly plugins: ReadonlyArray<ForgePlugin>;
  readonly loading: boolean;
  readonly error: string | null;
  readonly attempt: PluginConnectionAttempt | null;
  readonly attempts?: PluginConnectionAttempts;
  readonly onConnect: (pluginId: ForgePluginId) => void;
  readonly emptyLabel?: string;
  readonly detailsLink?: boolean;
}

function PluginConnectionRowSkeleton(props: { readonly label: string }): ReactElement {
  return (
    <div aria-label={props.label} className={PLUGIN_ROW_CLASS}>
      <Skeleton className="size-10 rounded-lg" />
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <Skeleton className="h-3.5 w-24 rounded-full" />
          <Skeleton className="h-4 w-16 rounded-full" />
        </div>
        <Skeleton className="mt-2 h-3 w-72 max-w-full rounded-full" />
      </div>
      <Skeleton className={`h-8 w-20 rounded-lg ${PLUGIN_ROW_CONTROL_CLASS}`} />
    </div>
  );
}

export function PluginConnectionList({
  plugins,
  loading,
  error,
  attempt,
  attempts = EMPTY_PLUGIN_CONNECTION_ATTEMPTS,
  onConnect,
  emptyLabel = "No matching plugins.",
  detailsLink = true,
}: PluginConnectionListProps): ReactElement {
  if (loading) {
    return (
      <div className="divide-y divide-border/60">
        <PluginConnectionRowSkeleton label="Loading Gmail plugin" />
        <PluginConnectionRowSkeleton label="Loading Google Calendar plugin" />
      </div>
    );
  }

  if (error && plugins.length === 0) {
    return (
      <div className="px-3 py-8 text-sm text-muted-foreground sm:px-4">
        {error}. Sign in to Forge, then refresh this page.
      </div>
    );
  }

  if (plugins.length === 0) {
    return <div className="px-3 py-8 text-sm text-muted-foreground sm:px-4">{emptyLabel}</div>;
  }

  return (
    <>
      {error ? (
        <p className="px-3 py-2 text-xs text-muted-foreground sm:px-4">
          Showing the last known plugin state. {error}
        </p>
      ) : null}
      <div className="divide-y divide-border/60">
        {plugins.map((plugin) => (
          <PluginRow
            key={plugin.id}
            plugin={plugin}
            attempt={attempts[plugin.id] ?? attempt}
            onConnect={onConnect}
            detailsLink={detailsLink}
          />
        ))}
      </div>
    </>
  );
}
